// Music and Games: the week in listening and in Steam play, as stat tiles. The
// number is the chart (this week's time), a quiet line says what it came from,
// and seven thin bars give the week's shape with today in the accent. Both read
// the library analytics the Insights pages use (online only), keep the last
// answer so a widget that comes back paints at once, and refresh every ten
// minutes while shown.
import { useEffect, useState } from 'react'
import { api } from '@/runtime'
import type { Analytics } from '@/db/types'
import { addDays, fmtMinutes, todayLocal } from '@/lib/time'
import { Skeleton } from '@/components/Skeleton'
import { Failed, cx } from '@/components/ui'
import type { WidgetSize } from './layout'
import { Frame, Quiet } from './widgets'

const cache = new Map<string, { at: number; data: Analytics }>()
const STALE = 5 * 60_000

/** This week of a metric: the last answer at once, a fresh one when it is due. */
const useWeek = (metric: string) => {
  const [data, setData] = useState<Analytics | undefined>(() => cache.get(metric)?.data)
  const [failed, setFailed] = useState<string | null>(null)
  const [nudge, setNudge] = useState(0)
  useEffect(() => {
    let live = true
    const load = () =>
      api
        .libraryAnalytics({ metric, days: 7 })
        .then((d) => {
          cache.set(metric, { at: Date.now(), data: d })
          if (live) {
            setData(d)
            setFailed(null)
          }
        })
        .catch((e: unknown) => {
          if (live) setFailed(e instanceof Error ? e.message : 'no answer')
        })
    const held = cache.get(metric)
    if (!held || nudge || Date.now() - held.at > STALE) void load()
    const t = window.setInterval(load, 10 * 60_000)
    return () => {
      live = false
      window.clearInterval(t)
    }
  }, [metric, nudge])
  return { data, failed, retry: () => setNudge((n) => n + 1) }
}

/** The last seven days, oldest first, with each day's minutes (0 where nothing was played). */
const lastSeven = (a: Analytics) => {
  const today = todayLocal()
  const byDay = new Map(a.series.map((s) => [s.at.slice(0, 10), s.total]))
  return Array.from({ length: 7 }, (_, i) => {
    const day = addDays(today, i - 6)
    return { day, minutes: Math.round(byDay.get(day) ?? 0) }
  })
}

const weekday = (day: string) =>
  new Date(`${day}T12:00:00`).toLocaleDateString(undefined, { weekday: 'short' })

/**
 * Seven bars on a baseline: thin, rounded at the top, 2px apart, past days in
 * the quiet line tone and today in the accent. An empty day keeps a stub on the
 * baseline so the week still reads as seven. Each bar names its day and time.
 */
const WeekBars = ({ days, className }: { days: { day: string; minutes: number }[]; className?: string }) => {
  const max = Math.max(1, ...days.map((d) => d.minutes))
  // plain boxes rather than a stretched SVG, so the rounded tops stay round at any width
  return (
    <div
      className={cx('flex items-end gap-[2px] h-6', className)}
      role="img"
      aria-label={`The last seven days: ${days.map((d) => `${weekday(d.day)} ${d.minutes ? fmtMinutes(d.minutes) : 'nothing'}`).join(', ')}`}
    >
      {days.map((d, i) => (
        <span
          key={d.day}
          title={`${weekday(d.day)}: ${d.minutes ? fmtMinutes(d.minutes) : 'nothing'}`}
          className={cx(
            'flex-1 max-w-6 rounded-t-[3px]',
            i === days.length - 1 ? 'bg-accent' : 'bg-line-strong',
          )}
          style={{ height: d.minutes ? `${Math.max(8, (d.minutes / max) * 100)}%` : '2px' }}
        />
      ))}
    </div>
  )
}

/** "38m more than last week", in the text colour: listening more or less is neither good nor bad. */
const versus = (now: number, before: number) => {
  const diff = Math.round(now - before)
  if (!before || Math.abs(diff) < 5) return undefined
  return `${fmtMinutes(Math.abs(diff))} ${diff > 0 ? 'more' : 'less'} than last week`
}

const Thumb = ({ src, round = false }: { src?: string; round?: boolean }) =>
  src ? (
    <img
      src={src}
      alt=""
      className={cx('h-12 w-12 shrink-0 object-cover', round ? 'rounded-full' : 'rounded-lg')}
      loading="lazy"
    />
  ) : null

const StatWidget = ({
  kind,
  metric,
  label,
  empty,
  size,
  editing,
}: {
  kind: 'music' | 'games'
  metric: string
  label: string
  empty: string
  size: WidgetSize
  editing: boolean
}) => {
  const { data, failed, retry } = useWeek(metric)
  const search = { metric }
  if (!data)
    return (
      <Frame kind={kind} title={label} to="/music/insights" search={search} editing={editing}>
        {failed ? (
          <Failed compact what={label.toLowerCase()} reason={failed} onRetry={retry} />
        ) : (
          <div className="flex-1 flex flex-col justify-end gap-2" aria-hidden>
            <Skeleton className="h-7 w-24" />
            <Skeleton className="h-6 w-full" />
          </div>
        )}
      </Frame>
    )
  const held = data.available.some((m) => m.key === metric)
  const minutes = Math.round(data.totals.total)
  const days = lastSeven(data)
  const music = kind === 'music'
  const topArtist = data.top_groups.artist?.[0]
  const top = data.top_items[0]
  // what the time came from: the artist for listening, the game for play
  const lead = music ? topArtist?.key : top?.title
  const leadImage = music ? topArtist?.image : top?.data.image
  const delta = versus(data.totals.total, data.previous.total)
  return (
    <Frame
      kind={kind}
      title={label}
      to="/music/insights"
      search={search}
      size={size}
      footer="Insights"
      editing={editing}
    >
      {!held || !minutes ? (
        <Quiet>{empty}</Quiet>
      ) : (
        <div className="flex-1 min-h-0 flex flex-col gap-2">
          <div className="flex items-start gap-3 min-w-0">
            <div className="min-w-0 flex-1">
              {/* the number is the chart: proportional figures, the sans at weight */}
              <div className="text-[26px] font-bold leading-none tracking-tight">{fmtMinutes(minutes)}</div>
              {lead && <div className="text-xs text-fg-2 truncate mt-1">{lead}</div>}
              {size !== 'small' && delta && <div className="text-xs text-faint truncate">{delta}</div>}
            </div>
            {size !== 'small' && <Thumb src={leadImage} round={music} />}
            {/* on the wider sizes the week sits beside the number, not stretched under it */}
            {size !== 'small' && <WeekBars days={days} className="w-32 h-11 shrink-0 self-end" />}
          </div>
          {size === 'large' && (
            <ul className="flex flex-col gap-1.5 min-h-0 overflow-hidden">
              {(music ? (data.top_groups.artist ?? []).slice(1, 5) : data.top_items.slice(1, 5)).map((x) => {
                const name = 'key' in x ? x.key : x.title
                return (
                  <li key={name} className="flex items-baseline gap-2 text-[13px]">
                    <span className="truncate flex-1">{name}</span>
                    <span className="text-faint shrink-0">{fmtMinutes(Math.round(x.total))}</span>
                  </li>
                )
              })}
            </ul>
          )}
          {size === 'small' && <WeekBars days={days} className="mt-auto" />}
        </div>
      )}
    </Frame>
  )
}

export const MusicWidget = ({ size, editing }: { size: WidgetSize; editing: boolean }) => (
  <StatWidget
    kind="music"
    metric="listened"
    label="Listening this week"
    empty="Nothing played this week."
    size={size}
    editing={editing}
  />
)

export const GamesWidget = ({ size, editing }: { size: WidgetSize; editing: boolean }) => (
  <StatWidget
    kind="games"
    metric="played_minutes"
    label="Playing this week"
    empty="No Steam play this week."
    size={size}
    editing={editing}
  />
)
